import React, { useEffect, useState, lazy, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import HeroLearningDemo from '../components/home/HeroLearningDemo';
import { coursesService, Course } from '../services/coursesService';
import { getSmallThumbnailUrl } from '../utils/mediaUrl';
import { BilingualTitle } from '../components/common/BilingualTitle';
import {
  useSeoTags,
  ORGANIZATION_JSON_LD,
  WEBSITE_JSON_LD,
} from '../hooks/useSeoTags';
import './HomePage.css';

// Lazy load the learner dashboard (only needed for authenticated users)
const LearnerDashboard = lazy(() => import('../components/home/LearnerDashboard'));

// Icons
const PlayIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 5v14l11-7z"/>
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);

// Learning mode icons (colored per mode)
const ModeIcons: Record<string, React.ReactNode> = {
  dictation: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
    </svg>
  ),
  shadowing: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
    </svg>
  ),
  comprehension: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
    </svg>
  ),
  vocabulary: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
    </svg>
  ),
  quiz: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  ),
  conversation: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  ),
};

// Step icons for "How It Works"
const StepIcons = [
  // Browse courses
  <svg key="browse" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>,
  // Try free
  <svg key="try" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>,
  // Master
  <svg key="master" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
  </svg>,
];

// ── Popular Courses Section ────────────────────────────────────
const PopularCoursesSection: React.FC = () => {
  const { t } = useTranslation('home');
  const navigate = useNavigate();
  const { user } = useAuth();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const languagePreference = user?.languagePreference;

  useEffect(() => {
    const load = async () => {
      try {
        const { courses: popular } = await coursesService.getCourses({
          sortBy: 'popular',
          limit: 6,
          // Only show courses the user can actually learn from (matching their native language)
          ...(languagePreference ? { translationLanguage: languagePreference } : {}),
        });
        setCourses(popular);
      } catch {
        // Non-critical section
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [languagePreference]);

  if (loading || courses.length === 0) return null;

  return (
    <section className="popular-courses-section">
      <div className="popular-courses-inner">
        <div className="popular-courses-header">
          <h2>{t('popularCourses.title')}</h2>
          <p>{t('popularCourses.subtitle')}</p>
        </div>
        <div className="popular-courses-grid">
          {courses.map((course) => (
            <div
              key={course.id}
              className="popular-course-card"
              onClick={() => navigate(`/courses/${course.id}`)}
            >
              <div className="popular-course-thumb">
                {course.thumbnailUrl || course.coverImageUrl ? (
                  <img
                    src={getSmallThumbnailUrl(course.thumbnailUrl || course.coverImageUrl)}
                    alt={course.title}
                    loading="lazy"
                  />
                ) : (
                  <div className="popular-course-thumb-placeholder">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
                      <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
                    </svg>
                  </div>
                )}
                {course.cefrLevel && (
                  <span className="popular-course-level">{course.cefrLevel}</span>
                )}
              </div>
              <div className="popular-course-info">
                <BilingualTitle
                  title={course.title}
                  titles={course.titles}
                  fallback=""
                  variant="card"
                  as="h3"
                  className="popular-course-title"
                />
                {course.creator?.fullName && (
                  <span className="popular-course-creator">{course.creator.fullName}</span>
                )}
                <div className="popular-course-meta">
                  {course.enrollmentCount != null && course.enrollmentCount > 0 && (
                    <span>{course.enrollmentCount} {t('popularCourses.learners', { defaultValue: '人學習' })}</span>
                  )}
                  {course.ratingAverage != null && course.ratingAverage > 0 && (
                    <span className="popular-course-rating">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="#f59e0b" stroke="none">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                      </svg>
                      {course.ratingAverage.toFixed(1)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="popular-courses-footer">
          <Link to="/courses" className="btn btn-outline">
            {t('popularCourses.viewAll', { defaultValue: '探索所有課程' })}
            <ArrowRightIcon />
          </Link>
        </div>
      </div>
    </section>
  );
};

// ── Guest HomePage (marketing landing page for non-authenticated users) ──
const GuestHomePage: React.FC = () => {
  const { t } = useTranslation('home');
  const howItWorksSteps = t('howItWorks.steps', { returnObjects: true }) as Array<{ title: string; description: string }>;
  const LEARNING_MODES = ['dictation', 'shadowing', 'comprehension', 'vocabulary', 'quiz', 'conversation'] as const;

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">{t('hero.title')}</h1>
          <p className="hero-subtitle">
            <span className="subtitle-line">{t('hero.subtitleLine1')}</span>
            <span className="desktop-separator">，</span>
            <span className="subtitle-line">{t('hero.subtitleLine2')}</span>
          </p>
          <div className="hero-actions">
            <Link to="/courses" className="btn btn-primary btn-lg">
              <PlayIcon />
              {t('hero.cta')}
            </Link>
            <Link to="/placement-test" className="btn btn-outline btn-lg">
              {t('hero.secondaryCta')}
            </Link>
          </div>
        </div>
        <div className="hero-visual">
          <HeroLearningDemo />
        </div>
      </section>

      {/* Placement Test Prompt */}
      <section className="placement-prompt-section">
        <div className="placement-prompt-card">
          <div className="placement-prompt-content">
            <h3>{t('placementPrompt.title')}</h3>
            <p>{t('placementPrompt.description')}</p>
          </div>
          <Link to="/placement-test" className="btn btn-primary placement-prompt-cta">
            {t('placementPrompt.cta')}
          </Link>
        </div>
      </section>

      {/* Learning Modes Section */}
      <section className="modes-section">
        <div className="section-header">
          <h2>{t('learningModes.title')}</h2>
          <p>{t('learningModes.subtitle')}</p>
        </div>
        <div className="modes-grid">
          {LEARNING_MODES.map((mode) => (
            <div key={mode} className={`mode-card mode-${mode}`}>
              <div className="mode-icon-wrap">
                {ModeIcons[mode]}
              </div>
              <h3>{t(`learningModes.${mode}.name`)}</h3>
              <p>{t(`learningModes.${mode}.description`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Popular Courses */}
      <PopularCoursesSection />

      {/* How It Works */}
      <section className="how-it-works-section">
        <div className="section-header">
          <h2>{t('howItWorks.title')}</h2>
          <p>{t('howItWorks.subtitle')}</p>
        </div>
        <div className="steps-grid">
          {howItWorksSteps.map((step, index) => (
            <div key={index} className="step-card">
              <div className="step-number">{index + 1}</div>
              <div className="step-icon">
                {StepIcons[index]}
              </div>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Trial CTA */}
      <section className="trial-cta-section">
        <div className="trial-cta-inner">
          <h2>{t('trialCta.title')}</h2>
          <p className="trial-cta-subtitle">{t('trialCta.subtitle')}</p>
          <div className="trial-cta-features">
            {(t('trialCta.features', { returnObjects: true }) as string[]).map((feature, i) => (
              <div key={i} className="trial-cta-feature">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                {feature}
              </div>
            ))}
          </div>
          <div className="trial-cta-actions">
            <Link to="/pricing" className="btn btn-primary btn-lg">
              {t('trialCta.cta')}
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="cta-section">
        <h2>{t('cta.title')}</h2>
        <p>{t('cta.subtitle')}</p>
        <div className="cta-actions">
          <Link to="/courses" className="btn btn-primary btn-lg">
            {t('cta.primary')}
          </Link>
          <Link to="/pricing" className="btn btn-outline btn-lg">
            {t('cta.secondary')}
          </Link>
        </div>
      </section>
    </div>
  );
};

// ── Main HomePage (router) ──────────────────────────────────────
const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation(['home', 'common']);

  // SEO is the same regardless of auth state — `/` is the same canonical URL
  // for crawlers, so we render the home-page tags in every branch.
  // JSON-LD: Organization + WebSite (the latter exposes SearchAction so Google
  // can surface our /courses?q= search box as a sitelink search.)
  const seoTags = useSeoTags({
    title: t('common:meta.title'),
    description: t('common:meta.description'),
    canonical: '/',
    jsonLd: [ORGANIZATION_JSON_LD, WEBSITE_JSON_LD],
  });

  // Check for pending placement test session (OAuth flow loses URL params)
  useEffect(() => {
    const pendingSession = sessionStorage.getItem('placementTestSession');
    if (pendingSession && isAuthenticated) {
      navigate(`/placement-test?session=${pendingSession}`, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  if (isLoading) {
    return (
      <>
        {seoTags}
        <div style={{ minHeight: '60vh' }} />
      </>
    );
  }

  if (isAuthenticated) {
    return (
      <>
        {seoTags}
        <Suspense fallback={<div style={{ minHeight: '60vh' }} />}>
          <LearnerDashboard />
        </Suspense>
      </>
    );
  }

  return (
    <>
      {seoTags}
      <GuestHomePage />
    </>
  );
};

export default HomePage;
